import { useAction } from "convex/react"
import { memo, useCallback, useRef, useState } from "react"
import { api } from "~convex/_generated/api"

// Module-level cache for token info
const previewCache = new Map<
	string,
	{
		name: string
		icon: string | null
		market_cap: number | null
		price_change_24h: number | null
	} | null
>()

function formatMarketCap(mcap: number): string {
	if (mcap >= 1e12) {
		return `$${(mcap / 1e12).toFixed(1)}T`
	}
	if (mcap >= 1e9) {
		return `$${(mcap / 1e9).toFixed(1)}B`
	}
	if (mcap >= 1e6) {
		return `$${(mcap / 1e6).toFixed(1)}M`
	}
	if (mcap >= 1e3) {
		return `$${(mcap / 1e3).toFixed(1)}K`
	}
	return `$${mcap.toFixed(0)}`
}

interface CashtagPreviewProps {
	children: React.ReactNode
	onClick?: (ticker: string) => void
	ticker: string
}

export const CashtagPreview = memo(function CashtagPreview({
	children,
	onClick,
	ticker,
}: CashtagPreviewProps) {
	const [info, setInfo] = useState(previewCache.get(ticker) ?? null)
	const [loaded, setLoaded] = useState(previewCache.has(ticker))
	const [visible, setVisible] = useState(false)
	const fetchTokenInfo = useAction(api.swapActions.getTokenInfo)
	const fetchingRef = useRef(false)
	const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const fetchIfNeeded = useCallback(() => {
		if (previewCache.has(ticker) || fetchingRef.current) {
			setInfo(previewCache.get(ticker) ?? null)
			setLoaded(true)
			return
		}
		fetchingRef.current = true
		fetchTokenInfo({ ticker })
			.then(result => {
				const entry = result
					? {
							name: result.name,
							icon: result.icon,
							market_cap: result.market_cap,
							price_change_24h: result.price_change_24h,
						}
					: null
				previewCache.set(ticker, entry)
				setInfo(entry)
				setLoaded(true)
			})
			.catch(() => {
				previewCache.set(ticker, null)
				setLoaded(true)
			})
			.finally(() => {
				fetchingRef.current = false
			})
	}, [ticker, fetchTokenInfo])

	const handleMouseEnter = useCallback(() => {
		fetchIfNeeded()
		hoverTimerRef.current = setTimeout(() => setVisible(true), 300)
	}, [fetchIfNeeded])

	const handleMouseLeave = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current)
			hoverTimerRef.current = null
		}
		setVisible(false)
	}, [])

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: hover preview, not interactive
		<span
			className="relative inline-block"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<button
				className="font-semibold text-app-green hover:underline"
				onClick={
					onClick
						? e => {
								e.stopPropagation()
								e.preventDefault()
								onClick(ticker)
							}
						: undefined
				}
				type="button"
			>
				{children}
			</button>
			{visible && loaded && info && (
				<div className="absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 animate-fade-in rounded-xl border border-app-border bg-app-surface p-3 shadow-xl">
					<div className="flex items-center gap-2.5">
						{info.icon ? (
							<img alt="" className="h-9 w-9 rounded-full" src={info.icon} />
						) : (
							<div className="h-9 w-9 rounded-full bg-app-surface-light" />
						)}
						<div className="min-w-0 flex-1">
							<div className="truncate font-semibold text-app-text text-sm">{info.name}</div>
							<div className="text-app-text-tertiary text-xs">
								${ticker}
								{info.market_cap != null && info.market_cap > 0 && (
									<span> · {formatMarketCap(info.market_cap)} MC</span>
								)}
							</div>
						</div>
						{info.price_change_24h != null && (
							<div
								className={`font-semibold text-sm ${info.price_change_24h >= 0 ? "text-app-green" : "text-app-red"}`}
							>
								{info.price_change_24h >= 0 ? "+" : ""}
								{info.price_change_24h.toFixed(1)}%
							</div>
						)}
					</div>
				</div>
			)}
		</span>
	)
})
